import { useState, type FormEvent } from "react";
import { Aviso, Botao, Campo, Cartao } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  CLASSIFICACOES,
  ROTULO_DA_CLASSIFICACAO,
  camposParaCorpo,
  linhasDeCampos,
  useAlterarOrganizacao,
  validarCampos,
  type OrganizacaoDoCadastro,
} from "../../nucleo/cadastro";
import { useVoltarParaHome } from "../../nucleo/voltarParaHome";
import { SelecaoDeSegmento } from "./SelecaoDeSegmento";

/** Dados da empresa, classificação e os campos adicionais que a equipe anota. */
export function DadosDaOrganizacao({
  organizacao,
  podeEditar,
}: {
  organizacao: OrganizacaoDoCadastro;
  podeEditar: boolean;
}) {
  const alterar = useAlterarOrganizacao(organizacao.id);
  const voltarParaHome = useVoltarParaHome();
  const [nomeFantasia, setNomeFantasia] = useState(organizacao.nome_fantasia ?? "");
  const [cidade, setCidade] = useState(organizacao.cidade ?? "");
  const [uf, setUf] = useState(organizacao.uf ?? "");
  const [segmento, setSegmento] = useState(organizacao.segmento ?? "");
  const [classificacao, setClassificacao] = useState(organizacao.classificacao ?? "");
  const [campos, setCampos] = useState(linhasDeCampos(organizacao.campos_adicionais));
  const [problema, setProblema] = useState<string | null>(null);

  function salvar(evento: FormEvent) {
    evento.preventDefault();
    const erro = validarCampos(campos);
    setProblema(erro);
    if (erro) return;
    alterar.mutate(
      {
        nome_fantasia: nomeFantasia.trim() || null,
        cidade: cidade.trim() || null,
        uf: uf.trim() || null,
        segmento: segmento.trim() || null,
        classificacao: classificacao || null,
        campos_adicionais: camposParaCorpo(campos),
      },
      { onSuccess: () => voltarParaHome("Dados da empresa salvos.") },
    );
  }

  return (
    <Cartao titulo="Dados da empresa" data-testid="dados-da-organizacao">
      <p className="mb-3">
        <b>{organizacao.razao_social}</b> · CNPJ {organizacao.cnpj_formatado}
      </p>
      <form onSubmit={salvar} noValidate>
        <div className="grid gap-3 md:grid-cols-2">
          <Campo
            rotulo="Nome fantasia"
            value={nomeFantasia}
            disabled={!podeEditar}
            onChange={(e) => setNomeFantasia(e.target.value)}
          />
          <SelecaoDeSegmento value={segmento} disabled={!podeEditar} onChange={setSegmento} />
          <Campo rotulo="Cidade" value={cidade} disabled={!podeEditar} onChange={(e) => setCidade(e.target.value)} />
          <Campo
            rotulo="UF"
            maxLength={2}
            value={uf}
            disabled={!podeEditar}
            onChange={(e) => setUf(e.target.value)}
          />
          <div className="campo">
            <label className="campo__rotulo" htmlFor="classificacao-da-organizacao">
              Classificação
            </label>
            <select
              id="classificacao-da-organizacao"
              className="campo__controle"
              value={classificacao}
              disabled={!podeEditar}
              onChange={(e) => setClassificacao(e.target.value)}
            >
              <option value="">Sem classificação</option>
              {CLASSIFICACOES.map((item) => (
                <option key={item} value={item}>
                  {ROTULO_DA_CLASSIFICACAO[item]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <h3 className="titulo-secao">Campos adicionais</h3>
        <p className="nota-rodape mb-2">
          Anotações da equipe sobre a empresa (ex.: ERP, número de filiais). O cliente não as vê.
        </p>
        {campos.map((campo, indice) => (
          <div key={indice} className="grid gap-3 md:grid-cols-[1fr_2fr_auto] items-end mb-2">
            <Campo
              rotulo={`Nome do campo ${indice + 1}`}
              value={campo.nome}
              disabled={!podeEditar}
              onChange={(e) =>
                setCampos(campos.map((item, i) => (i === indice ? { ...item, nome: e.target.value } : item)))
              }
            />
            <Campo
              rotulo={`Valor do campo ${indice + 1}`}
              value={campo.valor}
              disabled={!podeEditar}
              onChange={(e) =>
                setCampos(campos.map((item, i) => (i === indice ? { ...item, valor: e.target.value } : item)))
              }
            />
            {podeEditar ? (
              <Botao
                variante="fantasma"
                tamanho="pequeno"
                aria-label={`Remover o campo ${indice + 1}`}
                onClick={() => setCampos(campos.filter((_, i) => i !== indice))}
              >
                Remover
              </Botao>
            ) : null}
          </div>
        ))}
        {podeEditar ? (
          <Botao
            variante="fantasma"
            className="mb-3"
            onClick={() => setCampos([...campos, { nome: "", valor: "" }])}
            data-testid="novo-campo-adicional"
          >
            Adicionar campo
          </Botao>
        ) : null}
        {problema ? <Aviso tom="erro" className="mb-3">{problema}</Aviso> : null}
        {alterar.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(alterar.error)}</Aviso> : null}
        {podeEditar ? (
          <Botao type="submit" disabled={alterar.isPending} data-testid="salvar-organizacao">
            {alterar.isPending ? "Salvando…" : "Salvar"}
          </Botao>
        ) : null}
      </form>
    </Cartao>
  );
}
